import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { firstValueFrom, Subscription, filter } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { Option4OptimizationComponent } from './option4-optimization.component';

@Component({selector:'app-ivr-memory',standalone:true,imports:[CommonModule,FormsModule,RouterLink,Option4OptimizationComponent],
  templateUrl:'./ivr-memory.component.html',styleUrls:['./ivr-memory.component.scss']})
export class IvrMemoryComponent implements OnInit, OnDestroy {
  @ViewChild('pageHeading') pageHeading?:ElementRef<HTMLElement>;
  page='overview'; section=''; dirty=false; optimizationVisited=false; private navigation?:Subscription;
  readonly root='/rnd/ivr-memory';
  readonly pages=[{id:'overview',label:'Overview',description:'See service health and choose your next task.'},
    {id:'memory',label:'Memory settings',description:'Control what Saba remembers and how Honcho connects.'},
    {id:'improve',label:'Improve AI',description:'Review evidence, test a candidate, and release approved changes.'},
    {id:'calls',label:'Call review',description:'Inspect one call at a time, including replies and playback evidence.'},
    {id:'activity',label:'Activity',description:'Check recent memory events and delivery problems.'}];
  readonly settingsPages=[{id:'behavior',label:'Recall & replay'},{id:'retention',label:'Previous calls'},
    {id:'connection',label:'Connection'},{id:'model',label:'Reasoning model'},
    {id:'privacy',label:'Privacy & delivery'},{id:'responses',label:'AI replies'}];
  get currentPage(){return this.pages.find(p=>p.id===this.page)!;}
  get currentSettingsPage(){return this.settingsPages.find(p=>p.id===this.section);}
  turnPage=0; eventPage=0; readonly pageSize=1; callView='turns';
  get visibleTurns(){return (this.selectedCall?.turns || []).slice(this.turnPage*this.pageSize,(this.turnPage+1)*this.pageSize);}
  get turnPages(){return Math.max(1,Math.ceil((this.selectedCall?.turns?.length || 0)/this.pageSize));}
  get visibleEvents(){return (this.status?.events || []).slice(this.eventPage*10,(this.eventPage+1)*10);}
  get eventPages(){return Math.max(1,Math.ceil((this.status?.events?.length || 0)/10));}
  selectCall(){this.turnPage=0;this.callView='turns';}
  changeSection(section:string){void this.router.navigate([this.root,this.page,section]);}
  private readPage(){
    const child=this.route.firstChild?.snapshot;
    const page=child?.paramMap.get('page') || 'overview';
    this.page=this.pages.some(p=>p.id===page)?page:'overview';
    this.section=this.route.firstChild?.firstChild?.snapshot.paramMap.get('section') || (this.page==='memory'?'behavior':this.page==='improve'?'overview':'');
    if(this.page==='memory' && !this.settingsPages.some(p=>p.id===this.section))this.section='behavior';
    if(this.page==='improve')this.optimizationVisited=true;
    requestAnimationFrame(()=>this.pageHeading?.nativeElement.scrollIntoView({block:'start'}));
  }
  config:any=null; status:any=null; busy=false; error=''; notice=''; secret=''; sensitive=''; patterns='';
  selectedCallId=''; callFilter='';
  get calls():any[]{return (this.status?.review?.calls || []).filter((c:any)=>!this.callFilter || (c.id+' '+(c.callId || '')).toLowerCase().includes(this.callFilter.toLowerCase()));}
  get selectedCall():any{return this.status?.review?.calls?.find((c:any)=>c.id===this.selectedCallId);}
  private base=environment.apiHost+'/api/v1/rnd/ivr-memory';
  private options={headers:new HttpHeaders({'X-Skip-Spinner':'true'})};
  constructor(private http:HttpClient,private router:Router,private route:ActivatedRoute){}
  ngOnInit(){this.readPage();this.navigation=this.router.events.pipe(filter(e=>e instanceof NavigationEnd)).subscribe(()=>this.readPage());void this.load();}
  ngOnDestroy(){this.navigation?.unsubscribe();}
  reload(){if(!this.dirty || window.confirm('Discard unsaved memory settings and reload?'))void this.load();}
  async load(){
    this.busy=true; this.error='';
    try {
      this.config=await firstValueFrom(this.http.get<any>(this.base+'/config',this.options));
      this.sensitive=this.config.sensitiveInputs.join(', ');
      this.patterns=this.config.redactionPatterns.join('\n'); this.secret='';
      this.dirty=false;
      await this.refresh();
    } catch(e:any){this.error=e?.error?.detail || 'Unable to load memory settings. Check your access and the Option 4 service.';}
    finally{this.busy=false;}
  }
  async refresh(){
    try{this.status=await firstValueFrom(this.http.get<any>(this.base+'/status',this.options));
      if(!this.selectedCall && this.status?.review?.calls?.length){this.selectedCallId=this.status.review.calls[0].id;this.selectCall();}
      this.turnPage=Math.min(this.turnPage,this.turnPages-1);this.eventPage=Math.min(this.eventPage,this.eventPages-1);}
    catch(e:any){this.error=e?.error?.detail || 'Unable to check Honcho connectivity.';}
  }
  async save(){
    this.busy=true; this.error=''; this.notice='';
    const {apiKeyConfigured,...body}=this.config;
    try {
      this.config=await firstValueFrom(this.http.put<any>(this.base+'/config',{...body,apiKey:this.secret,
        sensitiveInputs:this.sensitive.split(',').map(v=>v.trim()).filter(Boolean),
        redactionPatterns:this.patterns.split('\n').map(v=>v.trim()).filter(Boolean)},this.options));
      this.secret=''; this.notice='Saved. Option 4 uses this revision on its next turn; no IVR or JAR restart is needed. Pending deliveries from the previous revision were discarded.';
      this.dirty=false;
      await this.refresh();
    }catch(e:any){this.error=e?.error?.detail || 'Save failed. Reload if another administrator changed these settings.';}
    finally{this.busy=false;}
  }
}
